import { getPreset } from "./presets"
import {
  DEFAULT_FONT_KEY,
  FONTS,
  normalizeFontKey,
  resolveFontPair,
} from "./fonts"
import {
  DEFAULT_RADIUS,
  type ColorModeTokens,
  type ResolvedTheme,
  type ThemeDefinition,
} from "./tokens"

/**
 * Merges a stored tenant theme (preset key + overrides + radius + fonts) into
 * a full ResolvedTheme. Unknown preset keys fall back to "default", unknown
 * font keys to the preset's own font, invalid radii to the default radius.
 *
 * Fonts resolve to a pair (heading + body): an explicit pair key wins, then
 * stored headingFont/font keys, then the preset's own fonts, then Inter.
 * Stored `outfit`/`geist` values keep rendering through normalizeFontKey.
 */
export function resolveTheme(
  theme: ThemeDefinition | null | undefined
): ResolvedTheme {
  const preset = getPreset(theme?.preset)
  const light = { ...preset.light, ...(theme?.overrides?.light ?? {}) }
  const dark = { ...preset.dark, ...(theme?.overrides?.dark ?? {}) }
  const radius =
    typeof theme?.radius === "number" && theme.radius >= 0 && theme.radius <= 2
      ? theme.radius
      : preset.radius

  const presetBody = normalizeFontKey(preset.font) ?? DEFAULT_FONT_KEY
  const presetHeading =
    normalizeFontKey(preset.headingFont) ?? presetBody
  const storedBody = normalizeFontKey(theme?.font)
  const storedHeading = normalizeFontKey(theme?.headingFont)

  let body = presetBody
  let heading = presetHeading
  if (storedBody || storedHeading) {
    body = storedBody ?? presetBody
    heading = storedHeading ?? body
  } else {
    const pair = resolveFontPair({ pair: theme?.fontPair })
    body = pair.body
    heading = pair.heading
  }

  return { light, dark, radius, font: body, headingFont: heading }
}

/** Strip characters that could break out of a CSS declaration. */
function safe(value: unknown): string {
  return typeof value === "string" ? value.replace(/[;{}]/g, "").trim() : ""
}

/**
 * shadcn HSL channel strings ("221.2 83.2% 53.3%") get the hsl() wrapper,
 * everything else (hex, hsl(), oklch()) is emitted as-is.
 */
function colorValue(v: string): string {
  const s = safe(v)
  if (!s) return ""
  return /^[0-9.]+ [-0-9.]+% [-0-9.]+%$/.test(s) ? `hsl(${s})` : s
}

function tokenDeclarations(t: ColorModeTokens): string[] {
  const d: string[] = []
  const push = (name: string, value: string) => {
    const v = colorValue(value)
    if (v) d.push(`${name}:${v};`)
  }

  // Semantic tokens (shadcn-style names).
  push("--background", t.background)
  push("--foreground", t.foreground)
  push("--card", t.card)
  push("--card-foreground", t.cardForeground)
  push("--popover", t.popover)
  push("--popover-foreground", t.popoverForeground)
  push("--primary", t.primary)
  push("--primary-foreground", t.primaryForeground)
  push("--secondary", t.secondary)
  push("--secondary-foreground", t.secondaryForeground)
  push("--muted", t.muted)
  push("--muted-foreground", t.mutedForeground)
  push("--accent", t.accent)
  push("--accent-foreground", t.accentForeground)
  push("--destructive", t.destructive)
  push("--destructive-foreground", t.destructiveForeground)
  push("--border", t.border)
  push("--input", t.input)
  push("--ring", t.ring)

  // @medusajs/ui mapping: recolors every ui-* class in the storefront.
  push("--bg-base", t.background)
  push("--bg-subtle", t.muted)
  push("--bg-component", t.card)
  push("--bg-field", t.popover)
  push("--fg-base", t.foreground)
  push("--fg-subtle", t.mutedForeground)
  push("--fg-muted", t.mutedForeground)
  push("--border-base", t.border)
  push("--border-strong", t.border)
  push("--fg-interactive", t.primary)
  push("--fg-interactive-hover", t.primaryHover)
  push("--bg-interactive", t.primary)
  push("--border-interactive", t.primary)
  push("--fg-on-color", t.primaryForeground)

  // Site blocks (Puck) variables.
  push("--site-accent", t.primary)
  push("--site-bg-muted", t.secondary)
  push("--site-bg-accent", t.contrast)
  push("--site-fg-accent", t.contrastForeground)

  return d
}

/**
 * Build the CSS text for a theme: semantic tokens plus the mappings onto
 * @medusajs/ui variables, site-blocks variables, --radius, --font-sans and
 * --font-heading. Light tokens go on :root, dark tokens on .dark. The same
 * string powers the storefront injector, the admin preview and the
 * site-page editor, so previews match the live site by construction.
 */
export function themeToCss(theme: ThemeDefinition | null | undefined): string {
  if (!theme) return ""
  const resolved = resolveTheme(theme)
  const shared = `--radius:${resolved.radius}rem;--font-sans:${
    FONTS[resolved.font as keyof typeof FONTS]?.cssVar ?? "var(--font-inter)"
  };--font-heading:${
    FONTS[resolved.headingFont as keyof typeof FONTS]?.cssVar ??
    "var(--font-inter)"
  };`

  const blocks = (["light", "dark"] as const).map((mode) => {
    const selector = mode === "light" ? ":root" : ".dark"
    return `${selector}{${shared}${tokenDeclarations(resolved[mode]).join("")}}`
  })
  return blocks.join("")
}

export { DEFAULT_RADIUS }
